import React, { useEffect, useRef } from 'react';
import { Skeleton } from 'primereact/skeleton';
import '../BreadcrumbSection/BreadcrumbSection.styles.scss';
import '../MoleculeHeaderSection/MoleculeHeaderSection.styles.scss';
import '../AvailableStrengthsProductVariantsSection/AvailableStrengthsProductVariantsSection.styles.scss';
import '../ANDAInformationSection/ANDAInformationSection.styles.scss';
import '../MarketInsightsSection/MarketInsightsSection.styles.scss';
import '../RegulatoryOverviewSection/RegulatoryOverviewSection.styles.scss';
import '../../../global.styles.scss';
import './MoleculeSearchSkeleton.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const MoleculeSearchSkeleton: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate wrapper height based on scaled content to prevent extra white space
  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    const updateWrapperHeight = () => {
      requestAnimationFrame(() => {
        if (!contentRef.current || !wrapperRef.current) return;

        const contentHeight = contentRef.current.scrollHeight || 0;
        const scaledHeight = contentHeight * 0.65;
        wrapperRef.current.style.height = `${scaledHeight}px`;
      });
    };

    const timeoutId = setTimeout(updateWrapperHeight, 100);
    window.addEventListener('resize', updateWrapperHeight);

    const resizeObserver = new ResizeObserver(() => {
      updateWrapperHeight();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWrapperHeight);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="molecule-search-page-wrapper" ref={wrapperRef}>
      <div className="molecule-search-page" ref={contentRef}>
        {/* Breadcrumb Section Skeleton */}
        <nav className="breadcrumb-section">
          <div className="container">
            <Skeleton width="20rem" height="1.5rem" />
          </div>
        </nav>

        {/* Molecule Header Section Skeleton */}
        <section className="molecule-header">
          <div className="container">
            <div className="molecule-title">
              <Skeleton shape="circle" size="6.25rem" />
              <div className="molecule-info">
                <Skeleton
                  width="clamp(15rem, 30vw, 25rem)"
                  height="4rem"
                  style={{ marginBottom: '1rem' }}
                />
                <div className="molecule-meta">
                  <Skeleton width="12rem" height="1.5rem" />
                  <Skeleton width="12rem" height="1.5rem" />
                  <Skeleton width="10rem" height="1.5rem" />
                </div>
              </div>
            </div>
            <div className="stats-summary">
              {[1, 2, 3, 4].map(index => (
                <div key={index} className="stat-item">
                  <Skeleton width="5rem" height="3rem" style={{ margin: '0 auto 0.5rem' }} />
                  <Skeleton width="8rem" height="1rem" style={{ margin: '0 auto' }} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <main className="container">
          {/* Available Strengths Section Skeleton */}
          <section className="content-section">
            <div className="section-header">
              <div className="section-title">
                <Skeleton shape="circle" size="1.5rem" />
                <Skeleton width="20rem" height="1.5rem" />
              </div>
              <div className="view-toggle">
                <Skeleton width="5rem" height="2.5rem" borderRadius="0.5rem" />
                <Skeleton width="5rem" height="2.5rem" borderRadius="0.5rem" />
              </div>
            </div>
            <div className="section-content">
              <Skeleton width="100%" height="25rem" borderRadius="0.75rem" />
            </div>
          </section>

          {/* ANDA Information Section Skeleton */}
          <section className="content-section">
            <div className="section-header">
              <div className="section-title">
                <Skeleton shape="circle" size="1.5rem" />
                <Skeleton width="15rem" height="1.5rem" />
              </div>
              <div className="anda-controls">
                <Skeleton width="12rem" height="1.5rem" />
                <div className="pagination-controls">
                  <Skeleton width="2.5rem" height="2.5rem" borderRadius="0.5rem" />
                  <Skeleton width="8rem" height="1.5rem" />
                  <Skeleton width="2.5rem" height="2.5rem" borderRadius="0.5rem" />
                </div>
              </div>
            </div>
            <div className="section-content">
              <Skeleton width="100%" height="30rem" borderRadius="0.75rem" />
            </div>
          </section>

          {/* Market Insights Section Skeleton */}
          {/* <section className="content-section">
          <div className="section-header">
            <div className="section-title">
              <Skeleton shape="circle" size="1.5rem" />
              <Skeleton width="18rem" height="1.5rem" />
            </div>
            <Skeleton width="30rem" height="1rem" />
          </div>
          <div className="section-content">
            <Skeleton width="100%" height="20rem" borderRadius="0.75rem" />
          </div>
        </section> */}

          {/* Regulatory Overview Section Skeleton */}
          {/* <section className="content-section">
          <div className="section-header">
            <div className="section-title">
              <Skeleton shape="circle" size="1.5rem" />
              <Skeleton width="20rem" height="1.5rem" />
            </div>
            <Skeleton width="35rem" height="1rem" />
          </div>
          <div className="section-content">
            <Skeleton width="100%" height="20rem" borderRadius="0.75rem" />
          </div>
        </section> */}
        </main>
      </div>
    </div>
  );
};

export default MoleculeSearchSkeleton;
